import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { authService } from "@/services/authService";
import { useAuth } from "@/context/AuthContext";
import { consumeRedirectAfterLogin } from "@/utils/functions";
import AuthLayout from "./AuthLayout";
import { logoLatest } from "@/assets/images";
import { BOOKING_CONSTRAINTS } from "@/constants/booking";
import { otpFromResponse } from "@/utils/otpFromResponse";
import { OTP_CHANNEL_UNAVAILABLE, channelNoun, otherChannelNoun } from "@/utils/otpChannel";
import {postLoginPath} from '@/utils/identityGate';

const OTP_SESSIONS_KEY = 'hopn_otp_sessions';
const OTP_ACTIVE_KEY = 'hopn_otp_active';

// Sessions stored as { [identifier]: { otpMethod, otpSentAt, attempts } }
function getAllSessions() {
  try {
    return JSON.parse(sessionStorage.getItem(OTP_SESSIONS_KEY)) || {};
  } catch { return {}; }
}

function getSession(identifier) {
  return getAllSessions()[identifier] || null;
}

function saveSession(identifier, data) {
  const all = getAllSessions();
  all[identifier] = data;
  sessionStorage.setItem(OTP_SESSIONS_KEY, JSON.stringify(all));
}

function getActiveIdentifier() {
  try {
    return JSON.parse(sessionStorage.getItem(OTP_ACTIVE_KEY)) || null;
  } catch { return null; }
}

function saveActiveIdentifier(otpMethod, identifier) {
  sessionStorage.setItem(OTP_ACTIVE_KEY, JSON.stringify({ otpMethod, identifier }));
}

function clearActiveIdentifier() {
  sessionStorage.removeItem(OTP_ACTIVE_KEY);
}

function getRemainingTime(session) {
  if (!session?.otpSentAt) return 0;
  const elapsed = Math.floor((Date.now() - session.otpSentAt) / 1000);
  return Math.max(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS - elapsed, 0);
}

export default function OtpVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const stateData = location.state || {};
  const active = getActiveIdentifier();

  const otpMethod = stateData.otpMethod || active?.otpMethod;
  // What the SERVER said about where the code went ('email' | 'sms'). Absent on a reload
  // (router state does not survive one), which is why channelNoun falls back to the method
  // — the two agree on every login path; the server's word is simply the authoritative one.
  const otpChannel = stateData.channel || null;
  const identifier = stateData.identifier || active?.identifier;

  const existingSession = identifier ? getSession(identifier) : null;
  const initialRemaining = getRemainingTime(existingSession);

  /* ⚠️ SEEDED FROM THE NAVIGATION STATE, which carries a code only for test accounts —
     see `utils/otpFromResponse`. A lazy initialiser rather than an effect: the boxes are
     filled on the FIRST render, so nothing flashes empty and the digits are simply there
     when the screen arrives. */
  const [otp, setOtp] = useState(() => stateData.prefill || ["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(
    stateData.otpMethod && !existingSession ? BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS : initialRemaining
  );
  const [canResend, setCanResend] = useState(
    !stateData.otpMethod && initialRemaining <= 0
  );

  // Save session on mount when coming fresh from signin
  useEffect(() => {
    if (otpMethod && identifier) {
      saveActiveIdentifier(otpMethod, identifier);

      if (stateData.otpMethod) {
        const existing = getSession(identifier);
        saveSession(identifier, {
          otpMethod,
          otpSentAt: Date.now(),
          attempts: (existing?.attempts || 0) + 1,
        });
        setResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS);
        setCanResend(false);
      }
    }
  }, []);

  // Redirect if no data at all
  useEffect(() => {
    if (!otpMethod || !identifier) {
      navigate("/signin", { replace: true });
    }
  }, [otpMethod, identifier, navigate]);

  // Auto-focus first input
  useEffect(() => {
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  }, []);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [canResend]);

  // Mirror of `otp` that is current DURING an event, before React re-renders. The focus
  // guard below runs synchronously off the back of a change, when `otp` still holds the
  // pre-keystroke value — reading state there would bounce focus back to the box the
  // passenger just filled.
  const otpNowRef = useRef(otp);
  useEffect(() => {
    otpNowRef.current = otp;
  }, [otp]);

  const handleOtpFocus = (idx) => {
    const now = otpNowRef.current;
    const firstEmpty = now.findIndex((d) => d === "");
    const target = firstEmpty === -1 ? now.length - 1 : firstEmpty;
    if (idx > target) {
      otpRefs.current[target]?.focus();
    }
  };

  const handleOtpChange = (value, idx) => {
    if (!/^[0-9]?$/.test(value)) return;
    const next = [...otp];
    next[idx] = value;
    otpNowRef.current = next;
    setOtp(next);
    setError("");
    // Focus SYNCHRONOUSLY, inside the input event. iOS Safari only keeps the on-screen
    // keyboard up when the next focus happens within the user gesture — deferred through
    // a timeout it closed the keyboard after every digit, so the passenger had to tap
    // each box to reopen it. Android and desktop never cared either way.
    if (value && otpRefs.current[idx + 1]) {
      otpRefs.current[idx + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Enter") {
      // Submit on keyboard Enter (previously the button was click-only).
      e.preventDefault();
      handleVerifyOtp();
      return;
    }
    if (e.key === "Backspace") {
      if (!otp[idx] && idx > 0) {
        const next = [...otp];
        next[idx - 1] = "";
        setOtp(next);
        otpRefs.current[idx - 1].focus();
      } else {
        const next = [...otp];
        next[idx] = "";
        setOtp(next);
      }
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowLeft" && idx > 0) otpRefs.current[idx - 1].focus();
    if (e.key === "ArrowRight" && idx < otp.length - 1) otpRefs.current[idx + 1].focus();
  };

  const handleOtpPaste = (e, idx) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedData) return;
    const digits = pastedData.split("");
    const nextOtp = [...otp];
    for (let i = 0; i < digits.length && idx + i < otp.length; i++) {
      nextOtp[idx + i] = digits[i];
    }
    setOtp(nextOtp);
    const lastIndex = Math.min(idx + digits.length - 1, otp.length - 1);
    otpRefs.current[lastIndex]?.focus();
  };

  const handleVerifyOtp = async () => {
    const code = otp.join("");
    if (code.length < 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");

    const result = await authService.verifyOtp(identifier, code, otpMethod);

    setLoading(false);

    if (result.success && result.data?.token) {
      clearActiveIdentifier();
      sessionStorage.removeItem(OTP_SESSIONS_KEY);
      login(result.data.token);
      // Ask for the missing field now rather than letting the booking be refused for it
      // later — see postLoginPath on why the pending redirect gives way.
      navigate(postLoginPath(result.data.user, consumeRedirectAfterLogin()), { replace: true });
    } else {
      setError(result.message || "Invalid OTP. Try again.");
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  };

  const handleResend = async () => {
    if (!canResend || resendLoading) return;
    setResendLoading(true);
    setResendMessage("Sending...");

    const resendEmail = otpMethod === "email" ? identifier : null;
    const resendPhone = otpMethod === "phone" ? identifier : null;
    const result = await authService.resendOtp(resendEmail, resendPhone);

    setResendLoading(false);

    if (result.success) {
      const existing = getSession(identifier);
      saveSession(identifier, {
        ...existing,
        otpSentAt: Date.now(),
        attempts: (existing?.attempts || 0) + 1,
      });
      // A resend gets the same treatment as the first send: a new code for a test
      // account replaces whatever is in the boxes.
      const resent = otpFromResponse(result.data);
      if (resent) setOtp(resent);
      // Name the channel — "resent successfully" left the passenger watching the wrong
      // inbox when the code had gone to their phone.
      setResendMessage(`OTP resent to your ${channelNoun(result.channel, otpMethod)}`);
      // Only start timer AFTER successful resend
      setCanResend(false);
      setResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS);
    } else if (result.key === OTP_CHANNEL_UNAVAILABLE) {
      // Nothing to resend TO. Going back is the only move that can work — the other
      // identifier is on the sign-in screen.
      setResendMessage(`We can't send a code to your ${channelNoun(otpChannel, otpMethod)}. Please go back and use your ${otherChannelNoun(otpMethod)}.`);
    } else {
      setResendMessage(result.message || "Failed to resend OTP");
      // Keep canResend true so user can retry
    }
    setTimeout(() => setResendMessage(""), 3000);
  };

  const handleGoBack = () => {
    navigate("/signin", { replace: true });
  };

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!otpMethod || !identifier) return null;

  return (
    <AuthLayout>
      <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar">
        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <div>
            {/* AuthLayout's branded panel is lg-only, so on a phone this screen carried no
                        logo at all. Same asset, shown only where that panel isn't. */}
            <div className="flex justify-center mb-6 lg:hidden">
              <img src={logoLatest} alt="hop'n" className="h-9 w-auto"/>
            </div>

            <div className="mb-5 sm:mb-8 text-center">
              <h1 className="mb-2 font-semibold text-gray-800 text-2xl sm:text-3xl">
                OTP Verification
              </h1>
              {/* ⚠️ FROM THE SERVER'S `data.channel` where we have it. Interpolating the
                  raw method printed "your phone" correctly but only by coincidence — the
                  form chose the identifier, so of course it matched. Since a code now goes
                  to ONE channel, the channel the backend reports is the thing to state. */}
              <p className="text-sm text-gray-500">
                We have sent an OTP to your {channelNoun(otpChannel, otpMethod)}
              </p>
            </div>

            <div>
              <h2 className="text-base font-medium text-gray-700 text-center mb-4">
                Enter OTP Code
              </h2>

              {/* OTP Fields */}
              {/* Grid, not a fixed-width row: six 48px boxes plus gaps overflowed a
                  narrow phone and the first/last were clipped. Columns share whatever
                  width there is and each box is capped at its old size. */}
              <div className="grid grid-cols-6 gap-2 sm:gap-3 justify-items-center mb-6">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (otpRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                    onFocus={() => handleOtpFocus(idx)}
                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                    onPaste={(e) => handleOtpPaste(e, idx)}
                    className="w-full max-w-12 min-w-0 h-12 sm:h-14 text-center text-lg font-semibold text-gray-800 border border-gray-300 rounded-xl bg-transparent focus:border-gray-500 focus:outline-none"
                  />
                ))}
              </div>

              {error && (
                <p className="text-sm text-red-500 text-center mb-4">{error}</p>
              )}

              {/* Resend */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <button
                  type="button"
                  disabled={!canResend || resendLoading}
                  onClick={handleResend}
                  className={`text-sm font-medium ${
                    canResend && !resendLoading
                      ? "text-gray-800 hover:text-gray-600 cursor-pointer"
                      : "text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Resend code
                </button>
                {resendTimer > 0 && (
                  <span className="text-sm text-gray-500">
                    {formatTimer(resendTimer)}
                  </span>
                )}
              </div>

              {resendMessage && (
                <p className="text-sm text-center text-gray-600 mb-4">
                  {resendMessage}
                </p>
              )}

              {/* Continue */}
              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={loading}
                className="relative flex items-center justify-center w-full h-14 rounded-full bg-gray-900 text-white text-sm font-medium transition hover:bg-gray-800 disabled:opacity-60"
              >
                {loading ? "Verifying..." : "Continue"}
                <span className="absolute right-2 flex items-center justify-center w-10 h-10 rounded-full bg-emerald-300">
                  <svg
                    className="size-5 text-gray-900"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M4.167 10h11.666M10.833 5l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>

              {/* Back */}
              <div className="text-center mt-5">
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="text-sm font-medium text-gray-800 hover:text-gray-600"
                >
                  &larr; Go back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
